/**
 * Estilo único das Áreas de Planejamento nos mapas (cadastro, operação, roteirização,
 * candidatos). A Zona de Risco tem marcação própria: borda vermelha tracejada mais grossa,
 * preenchimento hachurado e um ícone de alerta no centro.
 */
import L from "leaflet";
import { verticesDaArea, geojsonDaArea, rotuloArea } from "./areasGeometria";

export const COR_ZONA_RISCO = "#D32F2F";
export const COR_AREA_PADRAO = "#1565C0";
export const ICONE_ZONA_RISCO = "mdi-alert-octagon";
const PADRAO_HACHURA_ID = "merus-zona-risco-hachura";

export function isZonaRisco(area) {
  return !!area && Number(area.zona_risco) === 1;
}

export function corDaArea(area) {
  if (isZonaRisco(area)) return COR_ZONA_RISCO;
  return area && /^#[0-9A-Fa-f]{6}/.test(area.cor || "") ? area.cor : COR_AREA_PADRAO;
}

/**
 * Hachura da Zona de Risco: <pattern> SVG no <defs> do renderer do mapa. Sem SVG
 * (canvas), devolve false e o estilo cai para preenchimento sólido.
 */
export function garantirHachura(map) {
  if (!map || typeof document === "undefined") return false;
  const pane = map.getPanes && map.getPanes().overlayPane;
  const svg = pane && pane.querySelector("svg");
  if (!svg) return false;
  if (svg.querySelector("#" + PADRAO_HACHURA_ID)) return true;
  const ns = "http://www.w3.org/2000/svg";
  let defs = svg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS(ns, "defs");
    svg.insertBefore(defs, svg.firstChild);
  }
  const pattern = document.createElementNS(ns, "pattern");
  pattern.setAttribute("id", PADRAO_HACHURA_ID);
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  pattern.setAttribute("width", "10");
  pattern.setAttribute("height", "10");
  pattern.setAttribute("patternTransform", "rotate(45)");
  const fundo = document.createElementNS(ns, "rect");
  fundo.setAttribute("width", "10");
  fundo.setAttribute("height", "10");
  fundo.setAttribute("fill", COR_ZONA_RISCO);
  fundo.setAttribute("fill-opacity", "0.08");
  const linha = document.createElementNS(ns, "line");
  linha.setAttribute("x1", "0");
  linha.setAttribute("y1", "0");
  linha.setAttribute("x2", "0");
  linha.setAttribute("y2", "10");
  linha.setAttribute("stroke", COR_ZONA_RISCO);
  linha.setAttribute("stroke-width", "3");
  linha.setAttribute("stroke-opacity", "0.45");
  pattern.appendChild(fundo);
  pattern.appendChild(linha);
  defs.appendChild(pattern);
  return true;
}

/** Opções de estilo Leaflet (path) da área. */
export function estiloArea(area, { destaque = false } = {}) {
  if (isZonaRisco(area)) {
    return {
      color: COR_ZONA_RISCO,
      weight: destaque ? 4 : 3,
      dashArray: "8 6",
      opacity: 0.95,
      fillColor: COR_ZONA_RISCO,
      fillOpacity: 0.25,
      className: "merus-zona-risco",
    };
  }
  const cor = corDaArea(area);
  return {
    color: cor,
    weight: destaque ? 3 : 2,
    opacity: 0.9,
    fillColor: cor,
    fillOpacity: destaque ? 0.2 : 0.12,
    className: "merus-area-planejamento",
  };
}

/** Aplica a hachura nos paths da Zona de Risco (depois de adicionados ao mapa). */
export function aplicarHachura(map, layer, area) {
  if (!isZonaRisco(area) || !garantirHachura(map) || !layer) return;
  const aplicar = (l) => {
    if (l && l._path) {
      l._path.setAttribute("fill", "url(#" + PADRAO_HACHURA_ID + ")");
      l._path.setAttribute("fill-opacity", "1");
    }
  };
  if (typeof layer.eachLayer === "function") layer.eachLayer(aplicar);
  else aplicar(layer);
}

function centroide(vertices) {
  let lat = 0;
  let lng = 0;
  vertices.forEach((p) => {
    lat += p.lt;
    lng += p.lg;
  });
  return [lat / vertices.length, lng / vertices.length];
}

/** Marcador de alerta no centro da Zona de Risco (null para área comum). */
export function marcadorZonaRisco(area) {
  if (!isZonaRisco(area)) return null;
  const vertices = verticesDaArea(area);
  if (!vertices) return null;
  const icon = L.divIcon({
    className: "merus-zona-risco-icone",
    html:
      '<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;' +
      "border-radius:50%;background:#fff;border:2px solid " + COR_ZONA_RISCO + ';box-shadow:0 1px 4px rgba(0,0,0,.35)">' +
      '<i class="mdi ' + ICONE_ZONA_RISCO + '" style="color:' + COR_ZONA_RISCO + ';font-size:18px;line-height:1"></i></span>',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
  return L.marker(centroide(vertices), { icon, interactive: true, keyboard: false });
}

/**
 * Camada completa da área (polígono + ícone da zona de risco + tooltip), pronta para
 * L.layerGroup/addTo. `rotuloZona`: texto do tooltip para Zona de Risco.
 */
export function camadaDaArea(area, { destaque = false, rotuloZona = "", onClick = null } = {}) {
  const geojson = geojsonDaArea(area);
  if (!geojson) return null;
  const grupo = L.featureGroup();
  const poligono = L.geoJSON(geojson, { style: () => estiloArea(area, { destaque }) });
  const titulo = rotuloArea(area);
  const texto = isZonaRisco(area) && rotuloZona ? rotuloZona + ": " + titulo : titulo;
  poligono.bindTooltip(texto, { sticky: true });
  if (onClick) poligono.on("click", () => onClick(area));
  grupo.addLayer(poligono);
  const marcador = marcadorZonaRisco(area);
  if (marcador) {
    marcador.bindTooltip(texto, { direction: "top", offset: [0, -12] });
    if (onClick) marcador.on("click", () => onClick(area));
    grupo.addLayer(marcador);
  }
  grupo.on("add", () => {
    const map = grupo._map;
    if (map) aplicarHachura(map, poligono, area);
  });
  return grupo;
}

/** Cor do marcador da unidade conforme a situação em relação à área. */
export function corSituacaoUnidade(situacao) {
  if (situacao === "dentro") return "#2E7D32";
  if (situacao === "fora") return COR_ZONA_RISCO;
  return "#757575";
}
